import { useCallback, useEffect, useRef } from 'react';

const eventHandlersMap = new Map<string, Map<Function, EventListener>>();

export function useCustomEvent<EventType extends string, DataType = any>(
  eventType: EventType,
  eventHandler?: (event: CustomEvent<DataType>) => void,
  target?: EventTarget
) {
  const eventTargetRef = useRef<EventTarget>(target || document);
  const handlerRef = useRef<EventListener | undefined>(undefined);
  const prevHandlerRef = useRef<EventListener | undefined>(undefined); // To track the previous handler

  useEffect(() => {
    handlerRef.current = eventHandler as EventListener;
  }, [eventHandler]);

  useEffect(() => {
    const eventTarget = eventTargetRef.current;
    let eventHandlersForEvent = eventHandlersMap.get(eventType);
    if (!eventHandlersForEvent) {
      eventHandlersForEvent = new Map();
      eventHandlersMap.set(eventType, eventHandlersForEvent);
    }

    // When the handler changes, remove the previous handler from the event target and the map
    if (prevHandlerRef.current) {
      eventTarget.removeEventListener(eventType, prevHandlerRef.current);
      eventHandlersForEvent.delete(prevHandlerRef.current);
    }

    if (handlerRef.current && eventHandler) {
      if (!eventHandlersForEvent.has(eventHandler)) {
        eventHandlersForEvent.set(eventHandler, handlerRef.current);
        eventTarget.addEventListener(eventType, handlerRef.current);
      }
    }

    // Keep track of the current handler for comparison in the next render
    prevHandlerRef.current = handlerRef.current;

    return () => {
      if (eventHandler && eventHandlersForEvent?.has(eventHandler)) {
        const originalHandler = eventHandlersForEvent.get(eventHandler);
        eventTarget.removeEventListener(eventType, originalHandler!);
        eventHandlersForEvent.delete(eventHandler);
        if (eventHandlersForEvent.size === 0) {
          eventHandlersMap.delete(eventType);
        }
      }
    };
  }, [eventType, eventHandler]);

  const triggerEvent = useCallback(
    (detail?: DataType) => {
      const customEvent = new CustomEvent<DataType>(eventType, { detail });
      eventTargetRef.current.dispatchEvent(customEvent);
    },
    [eventType]
  );

  const removeEventHandler = useCallback(() => {
    const handlersToRemove = eventHandlersMap.get(eventType);
    if (handlersToRemove && eventHandler) {
      handlersToRemove.delete(eventHandler);
      eventTargetRef.current.removeEventListener(
        eventType,
        handlerRef.current!
      );
      if (handlersToRemove.size === 0) {
        eventHandlersMap.delete(eventType);
      }
    }
  }, [eventType, eventHandler]);

  return { triggerEvent, removeEventHandler };
}
